import { Button, CircularProgress, Dialog, Stack, type Theme, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import ModalV2Actions from '/@/components/bayer/modal-actions';
import ModalV2Header from '/@/components/bayer/modal-header';
import Spinner from '/@/components/spinner';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { daysFromOriginal, flowEventProps, useSeedRecommendationFlow } from '/@/views/recommendations/v2/flow-tracking';
import PhenologyTimeline, { PhenologyLegend } from './phenology-timeline';
import { isSimulatableDate, resolveSimulation, SIMULATION_DEBOUNCE_MS } from './simulate-planting-date-state';
import { type HybridPhenology, usePhenology } from './use-phenology';
import { usePlantingDateImpact } from './use-planting-date-impact';

/**
 * The planting-date sandbox: pick a date, simulate it, and either discard the result or ask to
 * apply it. Once open, the simulation follows the picker: every new date re-runs it, so the
 * timeline on screen always belongs to the date in the input. Applying is the tab's business — it invalidates the environment — so this only reports
 * the request upwards through `onApplyRequested`.
 *
 * The phone gets the same controls inside a modal, since the simulated timeline needs the room;
 * the desktop keeps them inline in the bordered panel.
 */
function SimulatePlantingDate({
    field,
    crop = 'corn',
    plantingDate,
    compact,
    onApplyRequested,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
    compact?: boolean;
    /**
     * Asks the tab to apply the date; the tab owns what applying does. `keepsEnvironment` says
     * whether the date leaves the environment and the chosen hybrids as they are.
     */
    onApplyRequested: (date: string, keepsEnvironment: boolean) => void;
}) {
    const [draftDate, setDraftDate] = useState<string | undefined>(plantingDate);
    /** The date the simulation query runs with; unset means the simulation is closed. */
    const [requestedDate, setRequestedDate] = useState<string | undefined>(undefined);
    /** Kept on screen, labelled, when the input holds a date that can't be simulated. */
    const [lastValidDate, setLastValidDate] = useState<string | undefined>(undefined);
    const [modalOpen, setModalOpen] = useState(false);
    const flow = useSeedRecommendationFlow();

    const open = requestedDate !== undefined;
    const debouncedDate = useDebounce(draftDate, SIMULATION_DEBOUNCE_MS);

    const simulation = usePhenology({ field, crop, plantingDate: requestedDate });
    // Served from the SWR cache: this date has already been fetched once.
    const lastValidSimulation = usePhenology({ field, crop, plantingDate: lastValidDate });

    const { status, canApply } = resolveSimulation({
        draftDate,
        debouncedDate,
        requestedDate,
        isLoading: simulation.isLoading,
        hybrids: simulation.hybrids,
        plantingDate,
    });

    // Typed dates wait for the debounce; picks from the calendar skip it through `onAccept`. The
    // debounce must have caught up, or opening mid-window would re-request the previous date.
    useEffect(() => {
        if (open && debouncedDate === draftDate && isSimulatableDate(debouncedDate)) setRequestedDate(debouncedDate);
    }, [open, debouncedDate, draftDate]);

    // Checked while the simulation is on screen, so it is usually settled by the time "Aplicar
    // fecha" is pressed.
    const impact = usePlantingDateImpact({ field, crop, plantingDate: canApply ? draftDate : undefined });

    // Reported per date run rather than per "Simular" tap: once open, the picker re-runs the
    // simulation on every new date, and each of those is another date the user tried.
    // biome-ignore lint/correctness/useExhaustiveDependencies: one report per date run
    useEffect(() => {
        if (!requestedDate) return;
        flow.simulations.current += 1;
        mpTrackEvent('Seed recommendation planting date simulated', {
            ...flowEventProps(flow),
            days_from_original: daysFromOriginal(plantingDate, requestedDate),
            simulation_number: flow.simulations.current,
        });
    }, [requestedDate]);

    useEffect(() => {
        if (status === 'ready' && requestedDate !== lastValidDate) setLastValidDate(requestedDate);
    }, [status, requestedDate, lastValidDate]);

    const canSimulate = useMemo(
        () => isSimulatableDate(draftDate) && draftDate !== plantingDate,
        [draftDate, plantingDate]
    );

    /**
     * The result block's height the last time it had content. While recalculating, the spinner
     * holds it, so the page doesn't shrink under the user and clamp their scroll position.
     */
    const resultRef = useRef<HTMLDivElement>(null);
    const settledHeight = useRef<number | undefined>(undefined);
    useLayoutEffect(() => {
        if (status !== 'pending' && resultRef.current) settledHeight.current = resultRef.current.offsetHeight;
    });

    /**
     * Each new date brings its simulation into view once it settles, timeline and buttons included.
     * `nearest` leaves the page alone when it is already fully on screen. On the desktop the target
     * wraps the actions too; in the phone's dialog they are pinned, so the result is enough.
     */
    const scrollTargetRef = useRef<HTMLDivElement>(null);
    const scrollOnSettle = useRef(false);
    useEffect(() => {
        if (requestedDate) scrollOnSettle.current = true;
    }, [requestedDate]);
    useEffect(() => {
        if (!scrollOnSettle.current || (status !== 'ready' && status !== 'failed')) return;
        scrollOnSettle.current = false;
        (scrollTargetRef.current ?? resultRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [status]);

    const discard = () => {
        setRequestedDate(undefined);
        setLastValidDate(undefined);
        settledHeight.current = undefined;
    };

    const closeModal = () => {
        setModalOpen(false);
        discard();
        setDraftDate(plantingDate);
    };

    const heading = (
        <Stack>
            <Typography variant='subtitle1' fontWeight={700} color='primary'>
                {_('Simular otra fecha de siembra')}
            </Typography>
            <Typography variant='body2' color='primary'>
                {_(
                    'Ajustá la fecha de siembra según el período crítico del cultivo, y anticipar la incidencia de plagas y el riesgo de heladas.'
                )}
            </Typography>
        </Stack>
    );

    const simulate = () => {
        setRequestedDate(draftDate);
        // The phone shows the run in the dialog; the desktop drops it into the panel below.
        if (compact) setModalOpen(true);
    };

    /** "Simular" only starts the simulation; once it is open, the picker drives it. */
    const showSimulateButton = compact ? !modalOpen : !open;

    /** `fill` is the dialog's copy, where the field takes the free width. */
    const pickerRow = (fill: boolean) => (
        <Stack direction='row' spacing={fill ? 1 : 2} alignItems='center'>
            <MuiDatePicker
                value={draftDate ? dayjs(draftDate) : null}
                // A half-typed date stays an invalid dayjs ('Invalid Date') so the field keeps its
                // sections; `isSimulatableDate` keeps it from reaching the query.
                onChange={(date) => setDraftDate(date ? date.format('YYYY-MM-DD') : undefined)}
                onAccept={(date) => {
                    const accepted = date?.isValid() ? date.format('YYYY-MM-DD') : undefined;
                    if (open && isSimulatableDate(accepted)) setRequestedDate(accepted);
                }}
                sx={fill ? { flex: 1, minWidth: 0 } : { width: compact ? 200 : 240 }}
            />
            {showSimulateButton && !fill && (
                <Button
                    variant='text'
                    color='primary'
                    disabled={!canSimulate}
                    onClick={simulate}
                    data-testid='phenology-simulate'
                >
                    {_('Simular')}
                </Button>
            )}
        </Stack>
    );

    const timeline = (hybrids: HybridPhenology[]) => (
        <Stack spacing={2}>
            <PhenologyTimeline hybrids={hybrids} compact={compact} />
            {!compact && <PhenologyLegend />}
        </Stack>
    );

    const renderResult = () => {
        if (status === 'pending') {
            return (
                <Stack
                    alignItems='center'
                    justifyContent='center'
                    py={4}
                    minHeight={settledHeight.current}
                    data-testid='phenology-simulation-loading'
                >
                    <Spinner />
                </Stack>
            );
        }
        if (status === 'ready') return timeline(simulation.hybrids);

        // The input can't be simulated: say so, and keep the last good run up under its own date.
        return (
            <Stack spacing={2}>
                <Alert severity='danger' data-testid='phenology-simulation-error'>
                    {status === 'invalid'
                        ? _('Ingresá una fecha de siembra válida para simular.')
                        : _('No pudimos simular esa fecha de siembra. Probá con otra fecha.')}
                </Alert>
                {lastValidDate && lastValidDate !== draftDate && (
                    <>
                        <Typography variant='body2' color='primary'>
                            {_('Última simulación válida: {{date}}', {
                                date: dayjs(lastValidDate).format('DD/MM/YYYY'),
                            })}
                        </Typography>
                        {timeline(lastValidSimulation.hybrids)}
                    </>
                )}
            </Stack>
        );
    };

    const result = open && <div ref={resultRef}>{renderResult()}</div>;

    /** Offered for as long as the simulation is open, so it can be closed mid-load or on error. */
    const actions = open && (
        <Stack direction='row' spacing={2} justifyContent={compact ? 'stretch' : 'flex-end'} width='100%'>
            <Button variant='text' color='secondary' fullWidth={compact} onClick={compact ? closeModal : discard}>
                {_('Descartar')}
            </Button>
            <Button
                variant='contained'
                color={'secondary'}
                fullWidth={compact}
                disabled={!canApply || impact === 'checking'}
                startIcon={impact === 'checking' ? <CircularProgress size={16} color='inherit' /> : undefined}
                onClick={() => {
                    if (!canApply || !draftDate || impact === 'checking') return;
                    // On the phone the confirmation modal replaces this one rather than stacking.
                    if (compact) setModalOpen(false);
                    // `canApply` guarantees the simulation on screen is the input's.
                    onApplyRequested(draftDate, impact === 'keeps');
                }}
                data-testid='phenology-apply-date'
            >
                {_('Aplicar fecha')}
            </Button>
        </Stack>
    );

    const panelSx = {
        borderRadius: '8px',
        border: '1px solid',
        borderColor: (theme: Theme) => theme.palette.divider,
        background: '#fcfbfb',
    };

    if (compact) {
        return (
            <>
                <Stack spacing={2} p={2} sx={panelSx}>
                    {heading}
                    {pickerRow(false)}
                </Stack>

                {/*
                 * `Dialog fullScreen` rather than `ModalV2`, following `FieldSelectMapModal`:
                 * `ModalV2`'s Container is hard-coded to a small centered card (absolute, top/left
                 * 50%, capped at 90dvh), so it can't fill the window.
                 */}
                <Dialog open={modalOpen} onClose={closeModal} fullScreen>
                    <ModalV2Header onClose={closeModal}>{_('Simular otra fecha de siembra')}</ModalV2Header>
                    <Stack spacing={3} p={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Typography variant='body2' color='primary'>
                            {_(
                                'Ajustá la fecha de siembra según el período crítico del cultivo, y anticipar la incidencia de plagas y el riesgo de heladas.'
                            )}
                        </Typography>
                        {pickerRow(true)}
                        {result}
                    </Stack>
                    {actions && <ModalV2Actions>{actions}</ModalV2Actions>}
                </Dialog>
            </>
        );
    }

    return (
        <Stack spacing={2} p={2} sx={panelSx}>
            {heading}
            {pickerRow(false)}
            {open && (
                <Stack spacing={2} ref={scrollTargetRef}>
                    {result}
                    {actions}
                </Stack>
            )}
        </Stack>
    );
}

export default SimulatePlantingDate;
